/**
 * `live-operator-takeover` capability schemas.
 *
 * Three shapes live in this file:
 *   1. `LiveSessionRow` — one row per active WebSocket connection in
 *      `nx-live-sessions-<env>`. PK = connectionId; GSI = (characterId, kind).
 *   2. `LiveSessionEvent` — discriminated union of messages pushed from
 *      server → operator over WebSocket.
 *   3. `OperatorOverrideRow` — one row per pending operator-injected reply
 *      in `nx-operator-overrides-<env>`. PK = sessionId; 5-minute TTL.
 *
 * The IntervenePayloadSchema validates the REST body of
 *   POST /api/admin/live/intervene
 * before the Lambda touches DDB or APIGW Management API.
 */
import { z } from 'zod';
/* ── 1. Connection row ─────────────────────────────────────────────── */
/**
 * Fan-side row: every embed chat opens a WS, every chat-history GET
 * opens a WS. Operator-side row: every `/admin/live` page open.
 *
 * Discriminator: `kind`. The optional fields make it explicit which
 * variant carries which identifier (fanId vs operatorSub).
 */
const LiveSessionRowCommon = {
    connectionId: z.string().min(1).max(128),
    characterId: z.string().min(1).max(64),
    /** PK of the LOGICAL session — `<fanId>#<characterId>` for fan rows;
     *  for operator rows it's a sentinel like `op:<operatorSub>` since
     *  operators don't own a single session. */
    sessionId: z.string().min(1).max(256),
    connectedAt: z.string().datetime(),
    lastActivityAt: z.string().datetime(),
    /** Unix epoch seconds; DDB TTL prunes rows past this. */
    expiresAt: z.number().int().positive(),
};
export const LiveSessionFanRowSchema = z.object({
    ...LiveSessionRowCommon,
    kind: z.literal('fan'),
    fanId: z.string().min(1).max(120),
});
export const LiveSessionOperatorRowSchema = z.object({
    ...LiveSessionRowCommon,
    kind: z.literal('operator'),
    operatorSub: z.string().min(1).max(120),
    /** Cross-check field — populated at $connect so subsequent intervene
     *  calls can verify the operator's customer scope without a second
     *  Cognito lookup. */
    customerId: z.string().min(1).max(120).optional(),
});
export const LiveSessionRowSchema = z.discriminatedUnion('kind', [
    LiveSessionFanRowSchema,
    LiveSessionOperatorRowSchema,
]);
/* ── 2. Events pushed over WebSocket ───────────────────────────────── */
/**
 * Server → operator events. All have `type` discriminator. Fan-side
 * receives a much smaller surface (only `reply` events, see below);
 * the full event surface is operator-targeted.
 */
export const SessionStartedEventSchema = z.object({
    type: z.literal('session-started'),
    sessionId: z.string(),
    characterId: z.string(),
    fanId: z.string(),
    /** Connection-time stamp. Used by operator UI to sort sessions. */
    connectedAt: z.string().datetime(),
});
export const TurnFinalizedEventSchema = z.object({
    type: z.literal('turn-finalized'),
    sessionId: z.string(),
    characterId: z.string(),
    fanId: z.string(),
    fanText: z.string(),
    aiText: z.string(),
    /** True when the AI side was filled by an operator override rather
     *  than by the LLM. Operator UI uses this to badge their own past
     *  interventions in the transcript. */
    operatorOverride: z.boolean().default(false),
    /** Cognito sub of the operator who intervened, present iff
     *  operatorOverride === true. */
    operatorSub: z.string().optional(),
    ts: z.string().datetime(),
});
export const OperatorIntervenedEventSchema = z.object({
    type: z.literal('operator-intervened'),
    sessionId: z.string(),
    characterId: z.string(),
    operatorSub: z.string(),
    text: z.string(),
    ts: z.string().datetime(),
});
export const SessionEndedEventSchema = z.object({
    type: z.literal('session-ended'),
    sessionId: z.string(),
    characterId: z.string(),
    fanId: z.string(),
    ts: z.string().datetime(),
});
export const LiveErrorEventSchema = z.object({
    type: z.literal('error'),
    code: z.string(),
    message: z.string(),
});
export const LiveSessionEventSchema = z.discriminatedUnion('type', [
    SessionStartedEventSchema,
    TurnFinalizedEventSchema,
    OperatorIntervenedEventSchema,
    SessionEndedEventSchema,
    LiveErrorEventSchema,
]);
/**
 * Fan-side incoming event — strictly the subset the embed cares about.
 * Currently just `reply`, used to render an operator-pushed reply via
 * the same code path as an LLM-returned reply.
 */
export const FanReplyEventSchema = z.object({
    type: z.literal('reply'),
    text: z.string().min(1).max(8000),
    /** Discriminator the fan client uses only for telemetry — never
     *  surfaced visually. Defaults to 'operator-override' since that's
     *  the only case where the server actively pushes a reply (LLM
     *  replies still come through the synchronous POST path). */
    source: z.literal('operator-override').default('operator-override'),
    /** Turn id assigned at persistence time; used by fan client to
     *  de-dupe against any catch-up GET on reconnect. */
    turnId: z.string().min(1).max(64),
});
/* ── 3. Pending operator override row ──────────────────────────────── */
export const OperatorOverrideRowSchema = z.object({
    /** Same shape as the fan's sessionId: `<fanId>#<characterId>`. PK. */
    sessionId: z.string().min(1).max(256),
    text: z.string().min(1).max(4000),
    operatorSub: z.string().min(1).max(120),
    /** Idempotency key supplied by admin client; same nonce → same row,
     *  so re-submits are no-ops. */
    nonce: z.string().min(1).max(64),
    writtenAt: z.string().datetime(),
    /** Unix epoch seconds; DDB TTL reaps after 5 minutes. */
    expiresAt: z.number().int().positive(),
});
/* ── 4. Conversation-turn `meta` convention ────────────────────────── */
/**
 * `nx-fan-conversation-turns-<env>` doesn't have a tracked zod schema —
 * the turn rows are written by `lib/fan-memory.ts::writeTurnPair`. The
 * `live-operator-takeover` capability adds two optional fields under
 * `meta`:
 *
 *   meta.operatorOverride: boolean — true iff the AI side of this turn
 *     was produced by an operator override rather than by the LLM
 *   meta.operatorSub: string — Cognito sub of the operator who
 *     intervened; required iff operatorOverride is true
 *
 * Memory summarisation + audit queries SHOULD branch on this flag.
 */
export const TURN_META_OPERATOR_OVERRIDE = 'operatorOverride';
export const TURN_META_OPERATOR_SUB = 'operatorSub';
/* ── 5. REST endpoint body validation ──────────────────────────────── */
export const IntervenePayloadSchema = z.object({
    /** Target session — must exist in `nx-live-sessions-<env>` (kind=fan)
     *  OR be a valid `<fanId>#<characterId>` even if fan is offline (the
     *  override is then persisted as a regular turn for next-load). */
    sessionId: z.string().min(1).max(256),
    /** Text the operator wants the AI to say. Same upper bound as a
     *  normal chat reply. Hard floor at 1 character to avoid empty
     *  pushes. */
    text: z.string().min(1).max(4000),
    /** Per-request idempotency token. Repeating the same nonce within
     *  24h is a no-op. Client SHOULD generate a UUIDv4. */
    nonce: z.string().min(8).max(64),
    /** Whether the fan surface should SPEAK this reply through the
     *  character's TTS pipeline. Default true (historical behaviour —
     *  every override was voiced); operators can send text-only with
     *  false. */
    voice: z.boolean().optional(),
});
